import { Head, Link, router, usePage } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { useMemo, useState } from 'react'
import { Plus, Eye, Edit, Users, Upload, Mail, AlertCircle, CheckCircle, Loader2, X, UserCheck } from 'lucide-react'
import DataTable, { Column } from '@/components/DataTable'
import AgentPasswordActions from '@/components/AgentPasswordActions'

interface Agent {
  row_key: string
  id: number
  kind: 'agent' | 'invite'
  email: string
  first_name: string
  last_name: string
  full_name: string
  onboarding_status: string | null
  referral_status?: string | null
  referring_agent_name?: string | null
  override_percentage?: number
  phone: string | null
  license_count: number | null
  sub_agent_count: number | null
  parent_agent_name: string | null
  status_sort?: string | null
  sponsor_sort?: string | null
  document_sent_at?: string | null
  document_signed_at?: string | null
  welcome_email_status?: 'never' | 'sent' | 'failed' | 'pending'
  welcome_email_at?: string | null
  welcome_email_error?: string | null
  has_signed_in?: boolean
  can_send_welcome?: boolean
  needs_welcome_email?: boolean
  created_at: string
}

interface Props extends PageProps {
  agents: Agent[]
  can_manage_passwords: boolean
  can_send_welcome_emails: boolean
  sendgrid_connected: boolean
}

export default function AgentsIndex({ agents, can_manage_passwords, can_send_welcome_emails, sendgrid_connected }: Props) {
  const { auth } = usePage<PageProps>().props
  const canImport = ['owner', 'administrator', 'staff'].includes(auth.user?.role || '')
  const showWelcomeTools = can_send_welcome_emails

  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [needsWelcomeOnly, setNeedsWelcomeOnly] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false)
  const [activating, setActivating] = useState(false)

  const visibleAgents = useMemo(
    () => (needsWelcomeOnly ? agents.filter(a => a.kind === 'agent' && a.needs_welcome_email) : agents),
    [agents, needsWelcomeOnly]
  )

  // Selection is deliberately not tied to welcome-email eligibility: an agent
  // who has already signed in cannot be welcomed but can still be activated.
  // Each bulk action decides for itself what it can act on, and the server
  // skips (and reports) the rest.
  const selectableIds = useMemo(
    () => visibleAgents.filter(a => a.kind === 'agent').map(a => a.id),
    [visibleAgents]
  )

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const selectedVisibleCount = selectableIds.filter(id => selectedSet.has(id)).length
  const allVisibleSelected = selectableIds.length > 0 && selectedVisibleCount === selectableIds.length
  const resendCount = agents.filter(
    a => a.kind === 'agent' && selectedSet.has(a.id) && a.welcome_email_status === 'sent'
  ).length

  const toggleAgent = (agentId: number) => {
    setSelectedIds(prev => (prev.includes(agentId) ? prev.filter(id => id !== agentId) : [...prev, agentId]))
  }

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(prev => prev.filter(id => !selectableIds.includes(id)))
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...selectableIds])))
    }
  }

  const handleSendWelcome = () => {
    if (selectedIds.length === 0 || sending) return
    setSending(true)
    router.post(
      '/agents/send_welcome_emails',
      { agent_ids: selectedIds },
      {
        preserveScroll: true,
        onFinish: () => {
          setSending(false)
          setConfirmOpen(false)
          setSelectedIds([])
        }
      }
    )
  }

  const handleBulkActivate = () => {
    if (selectedIds.length === 0 || activating) return
    setActivating(true)
    router.post(
      '/agents/bulk_activate',
      { agent_ids: selectedIds },
      {
        preserveScroll: true,
        onFinish: () => {
          setActivating(false)
          setActivateConfirmOpen(false)
          setSelectedIds([])
        }
      }
    )
  }

  const alreadyActiveCount = agents.filter(
    a => a.kind === 'agent' && selectedSet.has(a.id) && a.onboarding_status === 'active'
  ).length

  const welcomeSkipCount = agents.filter(
    a => a.kind === 'agent' && selectedSet.has(a.id) && !a.can_send_welcome
  ).length

  const selectedWithCredentialsCount = agents.filter(
    a => a.kind === 'agent' && selectedSet.has(a.id) && a.has_signed_in
  ).length

  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''

  const renderWelcomeStatus = (agent: Agent) => {
    if (agent.kind === 'invite') return <span className="text-chestnut-charcoal/40">—</span>
    if (agent.has_signed_in) {
      return <span className="text-xs text-chestnut-charcoal/50">Signed in already</span>
    }
    switch (agent.welcome_email_status) {
      case 'sent':
        return (
          <span className="inline-flex items-center gap-1 text-xs text-green-700">
            <CheckCircle className="h-3.5 w-3.5" />
            Sent {formatDate(agent.welcome_email_at)}
          </span>
        )
      case 'failed':
        return (
          <span
            className="inline-flex items-center gap-1 text-xs text-red-700"
            title={agent.welcome_email_error || undefined}
          >
            <AlertCircle className="h-3.5 w-3.5" />
            Failed
          </span>
        )
      case 'pending':
        return <span className="text-xs text-amber-700">Sending…</span>
      default:
        return <span className="text-xs text-chestnut-charcoal/50">Not sent</span>
    }
  }

  const statusColors: Record<string, string> = {
    prospect: 'bg-gray-100 text-gray-700',
    invited: 'bg-blue-100 text-blue-700',
    contract_pending: 'bg-yellow-100 text-yellow-700',
    documents_pending: 'bg-orange-100 text-orange-700',
    training_pending: 'bg-purple-100 text-purple-700',
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-red-100 text-red-700'
  }

  const referralStatusColors: Record<string, string> = {
    invited: 'bg-blue-100 text-blue-700',
    document_signed: 'bg-green-100 text-green-700',
    past_agent: 'bg-gray-100 text-gray-700'
  }

  const referralStatusLabels: Record<string, string> = {
    invited: 'Invited',
    document_signed: 'Document Signed',
    past_agent: 'Past Agent'
  }

  const selectionColumn: Column<Agent> = {
    key: 'select',
    header: (
      <input
        type="checkbox"
        checked={allVisibleSelected}
        onChange={toggleSelectAll}
        disabled={selectableIds.length === 0}
        className="rounded border-chestnut-charcoal/30"
        title="Select all agents on this list"
        onClick={(e) => e.stopPropagation()}
      />
    ),
    className: 'w-10',
    render: (agent) => (
      <div onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selectedSet.has(agent.id) && agent.kind === 'agent'}
          onChange={() => toggleAgent(agent.id)}
          disabled={agent.kind === 'invite'}
          className="rounded border-chestnut-charcoal/30 disabled:opacity-30"
          title={
            agent.kind === 'invite'
              ? 'Pending invites cannot be selected'
              : agent.has_signed_in
                ? 'This agent has already signed in — they can be activated, but no welcome email will be sent to them'
                : agent.can_send_welcome
                  ? undefined
                  : 'This agent can be activated, but cannot be sent a welcome email'
          }
        />
      </div>
    )
  }

  const welcomeColumn: Column<Agent> = {
    key: 'welcome_email_status',
    header: 'Welcome Email',
    sortable: true,
    render: renderWelcomeStatus
  }

  const columns: Column<Agent>[] = [
    ...(showWelcomeTools ? [selectionColumn] : []),
    {
      key: 'full_name',
      header: 'Agent',
      sortable: true,
      render: (agent) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-chestnut-charcoal">{agent.full_name}</span>
            {agent.kind === 'invite' && (
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full bg-chestnut-gold/15 text-chestnut-gold whitespace-nowrap">
                Pending Invite
              </span>
            )}
          </div>
          <div className="text-sm text-chestnut-charcoal/60">{agent.email}</div>
        </div>
      )
    },
    {
      key: 'sponsor_sort',
      header: 'Sponsor',
      sortable: true,
      render: (agent) => agent.kind === 'invite' ? (
        <div className="text-sm text-chestnut-charcoal/60">
          {agent.referring_agent_name ? `Invited by: ${agent.referring_agent_name}` : '—'}
        </div>
      ) : (
        <div className="text-sm text-chestnut-charcoal/60">
          {agent.parent_agent_name ? `Under: ${agent.parent_agent_name}` : '—'}
        </div>
      )
    },
    {
      key: 'status_sort',
      header: 'Status',
      sortable: true,
      render: (agent) => agent.kind === 'invite' ? (
        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${referralStatusColors[agent.referral_status || ''] || 'bg-gray-100 text-gray-700'}`}>
          {referralStatusLabels[agent.referral_status || ''] || agent.referral_status}
        </span>
      ) : (
        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${statusColors[agent.onboarding_status || ''] || 'bg-gray-100 text-gray-700'}`}>
          {(agent.onboarding_status || '').replace('_', ' ')}
        </span>
      )
    },
    ...(showWelcomeTools ? [welcomeColumn] : []),
    {
      key: 'license_count',
      header: 'Licenses',
      sortable: true,
      className: 'text-center',
      render: (agent) => agent.license_count == null
        ? <span className="text-chestnut-charcoal/40">—</span>
        : agent.license_count
    },
    {
      key: 'sub_agent_count',
      header: 'Sponsored Agents',
      sortable: true,
      className: 'text-center',
      render: (agent) => agent.sub_agent_count == null
        ? <span className="text-chestnut-charcoal/40">—</span>
        : agent.sub_agent_count
    },
    {
      key: 'actions',
      header: 'Actions',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (agent) => agent.kind === 'invite' ? (
        <div className="flex items-center justify-end gap-2">
          <Link
            href="/admin/referrals"
            className="text-xs font-medium text-chestnut-forest hover:text-chestnut-forest/80"
            title="View in Referrals"
            onClick={(e) => e.stopPropagation()}
          >
            View invite
          </Link>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-2">
          <Link
            href={`/agents/${agent.id}`}
            className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest"
            title="View"
          >
            <Eye className="h-4 w-4" />
          </Link>
          <Link
            href={`/agents/${agent.id}/edit`}
            className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest"
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </Link>
          <Link
            href={`/agents/${agent.id}/hierarchy`}
            className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest"
            title="View Hierarchy"
          >
            <Users className="h-4 w-4" />
          </Link>
          {can_manage_passwords && (
            <div onClick={(e) => e.stopPropagation()}>
              <AgentPasswordActions
                agentId={agent.id}
                agentName={agent.full_name}
                sendgridConnected={sendgrid_connected}
              />
            </div>
          )}
        </div>
      )
    }
  ]

  return (
    <AppLayout>
      <Head title="Agents" />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Agents</h1>
            <p className="text-chestnut-charcoal/70 mt-1">Manage your agent network</p>
          </div>
          <div className="flex items-center gap-2">
            {canImport && (
              <Link href="/agents/import" className="btn-secondary flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Import Agents
              </Link>
            )}
            <Link href="/agents/new" className="btn-primary flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Agent
            </Link>
          </div>
        </div>

        {showWelcomeTools && (
          <div className="card p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <label className="flex items-center gap-2 text-sm text-chestnut-charcoal">
              <input
                type="checkbox"
                checked={needsWelcomeOnly}
                onChange={(e) => setNeedsWelcomeOnly(e.target.checked)}
                className="rounded border-chestnut-charcoal/30"
              />
              Only show agents who need a welcome email
              <span className="text-chestnut-charcoal/50">(never signed in, never welcomed)</span>
            </label>

            <div className="flex items-center gap-3">
              {!sendgrid_connected && (
                <span className="text-xs text-chestnut-charcoal/60">
                  SendGrid is not connected — welcome emails are unavailable.
                </span>
              )}
              <button
                onClick={() => setActivateConfirmOpen(true)}
                disabled={selectedIds.length === 0}
                className="btn-secondary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Mark the selected agents active and switch their accounts on"
              >
                <UserCheck className="h-4 w-4" />
                Activate ({selectedIds.length})
              </button>
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={!sendgrid_connected || selectedIds.length === 0}
                className="btn-primary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                title={sendgrid_connected ? undefined : 'Connect SendGrid under Settings → Integrations first'}
              >
                <Mail className="h-4 w-4" />
                Send welcome email ({selectedIds.length})
              </button>
            </div>
          </div>
        )}

        <div className="card p-6">
          <DataTable
            data={visibleAgents}
            columns={columns}
            searchPlaceholder="Search agents..."
            searchKeys={['full_name', 'email', 'phone']}
            pageSize={10}
            emptyMessage={needsWelcomeOnly ? 'No agents are waiting for a welcome email.' : 'No agents yet. Add your first agent to get started.'}
            getRowKey={(agent) => agent.row_key}
            onRowClick={(agent) => { if (agent.kind !== 'invite') router.visit(`/agents/${agent.id}`) }}
          />
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg">
            <div className="flex items-start justify-between px-6 py-4 border-b border-chestnut-charcoal/10">
              <h2 className="text-lg font-semibold text-chestnut-charcoal">
                Send welcome email to {selectedIds.length} {selectedIds.length === 1 ? 'agent' : 'agents'}?
              </h2>
              <button
                onClick={() => setConfirmOpen(false)}
                className="p-1 text-chestnut-charcoal/50 hover:text-chestnut-charcoal"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm text-chestnut-charcoal/80">
              <p>
                Each selected agent gets a brand new <strong>temporary password</strong>, which replaces
                whatever password is currently on their account, plus a link to sign in.
              </p>
              {resendCount > 0 && (
                <p className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                  {resendCount} of these {resendCount === 1 ? 'agent has' : 'agents have'} already received a
                  welcome email. Sending again replaces the password from that earlier email, so the old one
                  will stop working.
                </p>
              )}
              {welcomeSkipCount > 0 && (
                <p>
                  {welcomeSkipCount} of the selected {welcomeSkipCount === 1 ? 'agent' : 'agents'} will be
                  skipped — they have already signed in, or have no email address on file.
                </p>
              )}
              <p>
                Agents who have already signed in are never included, so nobody's working password is
                replaced. Sending happens in the background — outcomes appear on this list and in the email
                activity log.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-chestnut-charcoal/10">
              <button onClick={() => setConfirmOpen(false)} className="btn-secondary" disabled={sending}>
                Cancel
              </button>
              <button onClick={handleSendWelcome} className="btn-primary flex items-center gap-2" disabled={sending}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Send welcome email
              </button>
            </div>
          </div>
        </div>
      )}

      {activateConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg">
            <div className="flex items-start justify-between px-6 py-4 border-b border-chestnut-charcoal/10">
              <h2 className="text-lg font-semibold text-chestnut-charcoal">
                Activate {selectedIds.length} {selectedIds.length === 1 ? 'agent' : 'agents'}?
              </h2>
              <button
                onClick={() => setActivateConfirmOpen(false)}
                className="p-1 text-chestnut-charcoal/50 hover:text-chestnut-charcoal"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm text-chestnut-charcoal/80">
              <p>
                Each selected agent is marked <strong>Active</strong> and their account is
                <strong> switched on</strong>, so they are allowed to sign in.
              </p>
              <p>
                No email is sent and no password is created. Agents who already have a password can sign in
                straight away; anyone who has never been welcomed still needs a welcome email before they
                can get in.
              </p>
              {selectedWithCredentialsCount > 0 && (
                <p className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                  {selectedWithCredentialsCount} of these agents {selectedWithCredentialsCount === 1 ? 'has' : 'have'}{' '}
                  signed in before, so activating them restores their access immediately.
                </p>
              )}
              {alreadyActiveCount > 0 && (
                <p className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                  {alreadyActiveCount} of these {alreadyActiveCount === 1 ? 'agent is' : 'agents are'} already
                  active and will be left alone.
                </p>
              )}
              <p>
                Agents you deliberately deactivated are skipped — reactivate those one at a time from their
                agent page.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-chestnut-charcoal/10">
              <button onClick={() => setActivateConfirmOpen(false)} className="btn-secondary" disabled={activating}>
                Cancel
              </button>
              <button onClick={handleBulkActivate} className="btn-primary flex items-center gap-2" disabled={activating}>
                {activating ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
                Activate
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
